import React from 'react';
import { useNavigate } from 'react-router-dom';
import { XCircle, ArrowLeft, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import ThemeToggle from '../components/ThemeToggle';
import { useBrandSettings } from '../context/BrandSettingsContext';
import BrandLogo from '../components/BrandLogo';

const UpgradeCancelPage = () => {
  const navigate = useNavigate();
  const { logos } = useBrandSettings();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            {logos?.default ? (
              <img src={logos.default} alt="KaiCheckIt" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl shadow-lg object-contain" />
            ) : (
              <BrandLogo size="responsive" data-testid="brand-logo-header" />
            )}
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <ThemeToggle />
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-lg mx-auto">
          <Card className="border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl">
            <CardHeader className="text-center pb-4">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center bg-slate-100 dark:bg-slate-700">
                <XCircle className="w-10 h-10 text-slate-500 dark:text-slate-400" />
              </div>
              
              <CardTitle className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">
                Payment Cancelled
              </CardTitle>
              
              <CardDescription className="text-base text-slate-600 dark:text-slate-400 mt-2">
                No worries! Your payment was not processed.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Reassurance Message */}
              <div className="bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg p-4">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  You can still use KaiCheckIt with our free tier (5 verifications per month). 
                  When you're ready, Pro access is just $27 one-time.
                </p>
              </div>

              {/* Free Tier Benefits */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                  Free Tier Includes:
                </h4>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <li>✓ 5 verifications per month</li>
                  <li>✓ Text, image & video claims</li>
                  <li>✓ AI-powered analysis</li>
                  <li>✓ Share results</li>
                  <li>✓ No ads</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/')}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-6 text-lg"
                >
                  Continue with Free
                </Button>
                
                <Button
                  onClick={() => navigate('/pricing')}
                  variant="outline"
                  className="w-full py-6 text-lg border-2"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  View Pricing Again
                </Button>
              </div>
              
              {/* Help Note */}
              <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <HelpCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Having trouble with payment? Contact us at{' '}
                  <a href="mailto:support@kaicheckit.com" className="font-medium hover:underline">
                    support@kaicheckit.com
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default UpgradeCancelPage;
